import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Select, MenuItem,
  FormControl, InputLabel, Button, Badge, IconButton, Alert,
  TextField, InputAdornment, Grid
} from '@mui/material';
import { 
  Refresh, ArrowUpward, ArrowDownward, NotInterested,
  Notifications, CheckCircle, LocalShipping, DoneAll, VolumeUp, VolumeOff,
  Search as SearchIcon, Clear as ClearIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import notification from "../assets/notification.mp3";
import { formatPrice } from '../components/CarrancyTrans';

const AdminOrdersPage = ({keywordsData, language}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortNewest, setSortNewest] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingAlert, setPendingAlert] = useState(0);
  
  // Search states
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [customerNameSearch, setCustomerNameSearch] = useState('');
  const [searchTimer, setSearchTimer] = useState(null);
  
  const newFirstTxt = language === 'ar' ? 'الأحدث أولا' : 'Newest First';
  const oldFirstTxt = language === 'ar' ? 'الأقدم أولا' : 'Oldest First';
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSound_pinding');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const audioRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(notification);
    audioRef.current.load();
    
    const enableAudio = () => {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {}).then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        });
      }
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
    
    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, []);

  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
      
      return true;
    } catch (error) {
      console.log('Web Audio API not supported or blocked');
      return false;
    }
  };

  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    const now = Date.now();
    if (now - lastNotificationTimeRef.current < 2000) {
      return;
    }
    
    lastNotificationTimeRef.current = now;
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play()
        .catch(() => playBeepSound());
    } else {
      playBeepSound();
    }
  };

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('notificationSound_pinding', JSON.stringify(newValue));
      return newValue;
    });
  };

  const statusColors = {
    canceled: 'error',
    pending: 'warning',
    processing: 'info',
    shipped: 'primary',
    delivered: 'success'
  };

  const statusTxt = {
    canceled: language === 'ar' ? 'ملغي' : 'Canceled',
    pending: language === 'ar' ? 'معلق' : 'Pending',
    processing: language === 'ar' ? 'قيد التحضير' : 'Preparation',
    shipped: language === 'ar' ? 'قيد التوصيل' : 'On road',
    delivered: language === 'ar' ? 'مكتمل' : 'Complete'
  };

  const statusIcons = {
    pending: <Notifications color="warning" />,
    processing: <CheckCircle color="info" />,
    shipped: <LocalShipping color="primary" />,
    delivered: <DoneAll color="success" />,
    canceled: <NotInterested color="error" />
  };

  // Debounced search function
  const handleSearch = (searchOrderId, searchCustomerName) => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    
    const timer = setTimeout(() => {
      fetchOrders(searchOrderId, searchCustomerName);
    }, 500); // 500ms debounce
    
    setSearchTimer(timer);
  };

  const handleOrderIdSearchChange = (e) => {
    const value = e.target.value;
    setOrderIdSearch(value);
    handleSearch(value, customerNameSearch);
  };

  const handleCustomerNameSearchChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearch(value);
    handleSearch(orderIdSearch, value);
  };

  const clearOrderIdSearch = () => {
    setOrderIdSearch('');
    fetchOrders('', customerNameSearch);
  };

  const clearCustomerNameSearch = () => {
    setCustomerNameSearch('');
    fetchOrders(orderIdSearch, '');
  };

  const clearAllSearches = () => {
    setOrderIdSearch('');
    setCustomerNameSearch('');
    fetchOrders('', '');
  };

  const fetchOrders = async (searchOrderId = orderIdSearch, searchCustomerName = customerNameSearch) => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      params.append('newest', sortNewest);
      
      // Add search params
      if (searchOrderId) params.append('orderId', searchOrderId);
      if (searchCustomerName) params.append('customerName', searchCustomerName);
      
      const response = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(response.data);
      setError(null);
      
      // Get stats for the first load
      const statsRes = await api.get('/admin/orders/stats');
      setStats(statsRes.data);
      setPendingAlert(statsRes.data.pendingCount);
        
      if (statsRes.data.pendingCount > 0) {
        playNotificationSound();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
      console.error('Orders fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders(); // Refresh data
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchOrders();
      
      // Set up periodic refresh (every 2 minutes)
      const interval = setInterval(() => fetchOrders(), 120000);
      return () => clearInterval(interval);
    }
  }, [user, filter, sortNewest]);

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Admin access required</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, mb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{language === 'ar' ? 'إدارة الطلبات' : 'Orders Management'}</Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={toggleSound} sx={{ color: soundEnabled ? '#FFD700' : '#B3B3B3' }}>
            {soundEnabled ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={() => fetchOrders()}
            disabled={loading}
          >
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
          
          <Badge badgeContent={pendingAlert} color="error" overlap="circular">
            <Chip 
              icon={<Notifications />}
              label={language === 'ar' ? 'طلبات معلقة' : 'Pending orders'}
              color="warning"
              variant={filter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setFilter('pending')}
              clickable
            />
          </Badge>
        </Box>
      </Box>

      {/* Search Boxes */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label={language === 'ar' ? 'البحث برقم الطلب' : 'Search by Order ID'}
              value={orderIdSearch}
              onChange={handleOrderIdSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
                endAdornment: orderIdSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearOrderIdSearch}>
                      <ClearIcon sx={{ color: '#B3B3B3', fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { color: '#FFFFFF' }
              }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
          </Grid>
          
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label={language === 'ar' ? 'البحث باسم العميل' : 'Search by Customer Name'}
              value={customerNameSearch}
              onChange={handleCustomerNameSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
                endAdornment: customerNameSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearCustomerNameSearch}>
                      <ClearIcon sx={{ color: '#B3B3B3', fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { color: '#FFFFFF' }
              }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            {(orderIdSearch || customerNameSearch) && (
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAllSearches}
                sx={{ borderColor: '#FFD700', color: '#FFD700' }}
              >
                {language === 'ar' ? 'مسح البحث' : 'Clear Search'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'فلترة' : 'Filter'}</InputLabel>
          <Select
            value={filter}
            label={language === 'ar' ? 'فلترة' : 'Filter'}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ color: '#FFFFFF' }}
          >
            <MenuItem value="all">{language === 'ar' ? 'الكل' : 'All'}</MenuItem>
            <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
            <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
            <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
            <MenuItem value="delivered">{language === 'ar' ? 'مكتملة' : 'Complete'}</MenuItem>
            <MenuItem value="canceled">{language === 'ar' ? 'ملغية' : 'Canceled'}</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          endIcon={sortNewest ? <ArrowDownward /> : <ArrowUpward />}
          onClick={() => setSortNewest(!sortNewest)}
          sx={{ borderColor: '#FFD700', color: '#FFD700' }}
        >
          {language === 'ar' ? 'ترتيب حسب' : 'Sort by'}: {sortNewest ? newFirstTxt : oldFirstTxt}
        </Button>

        {stats && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
            {stats.stats.map((stat) => (
              <Chip
                key={stat.status}
                icon={statusIcons[stat.status]}
                label={`${statusTxt[stat.status]}: ${stat.count}`}
                color={statusColors[stat.status]}
                variant={filter === stat.status ? 'filled' : 'outlined'}
                onClick={() => setFilter(stat.status)}
                clickable
              />
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={3} sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'رقم الطلب' : 'Order ID'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'العميل' : 'Customer'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'المنتجات' : 'Products'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'الإجمالي' : 'Total'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'التاريخ' : 'Date'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'طريقة الدفع' : 'payment method'}</TableCell>
              <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'جار تحميل الطلبات ...' : 'Loading orders ...'}
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'لا يوجد طلبات جديدة' : 'No new orders yet'}
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} sx={{ '&:hover': { backgroundColor: '#1A1A1A' } }}>
                  <TableCell sx={{ color: '#FFFFFF' }}>#{order.id}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography sx={{ color: '#FFFFFF' }}>{order.user?.username || 'Guest'}</Typography>
                      <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                        {order.user?.email || 'No email'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {order.items?.slice(0, 2).map((item) => (
                      <Typography key={item.id} sx={{ color: '#FFFFFF' }}>
                        {item.quantity}x {language === 'ar' ? item.product.name : item.product.name_en}
                      </Typography>
                    ))}
                    {order.items?.length > 2 && (
                      <Typography sx={{ color: '#B3B3B3' }}>
                        +{order.items.length - 2} {language === 'ar' ? 'أكثر' : 'more'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#FFD700' }}>{formatPrice(order.total_amount,order.currency,language)}</TableCell>
                  <TableCell sx={{ color: '#B3B3B3' }}>
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusTxt[order.status]}
                      color={statusColors[order.status]}
                      icon={statusIcons[order.status]}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.payment_method === 'cash_on_delivery' ? (language === 'ar' ? 'نقدي' : 'Cash') : 
                             order.payment_method === 'stripe' ? 'Stripe' : 'Visa'}
                      sx={{ 
                        backgroundColor: order.payment_method === 'cash_on_delivery' ? '#2D2D2D' : '#FFD700',
                        color: order.payment_method === 'cash_on_delivery' ? '#FFFFFF' : '#000000'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        disabled={order.status === 'delivered' || order.status === 'canceled'}
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                        sx={{ color: '#FFFFFF' }}
                      >
                        <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
                        <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
                        <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
                        <MenuItem value="delivered">{language === 'ar' ? 'مكتملة' : 'Complete'}</MenuItem>
                        <MenuItem value="canceled">{language === 'ar' ? 'ملغية' : 'Canceled'}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="view"
                      sx={{ color: '#FFD700' }}
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                    >
                      {language === 'ar' ? <ArrowBackIcon /> : <ArrowForward />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Search results count */}
      {(orderIdSearch || customerNameSearch) && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography sx={{ color: '#B3B3B3' }}>
            {language === 'ar' 
              ? `تم العثور على ${orders.length} نتيجة للبحث`
              : `Found ${orders.length} results for your search`}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AdminOrdersPage;